import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  input,
  OnInit,
  output,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  switchMap,
  zip,
} from 'rxjs';
import { Store } from '@ngrx/store';
import { v4 as uuid } from 'uuid';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { currentOfferSelection } from '@fcom/booking/store/selectors/booking-cart-state.selector';
import { boundsCurrency, globalBookingFlights } from '@fcom/common/store';
import { selectedInboundFareFamily, selectedOutboundFareFamily } from '@fcom/common/store/selectors/bounds.selector';
import { BookingAndAppState } from '@fcom/common/interfaces/booking';
import { SeatSelectionForFlight } from '@fcom/common/interfaces/seat-map.interface';
import { isFinnairNorraOrWetLease, PreciseNumber, ReplaceParams, resolveTailNames } from '@fcom/core/utils';
import { cartData, cartState } from '@fcom/booking/store/selectors';
import {
  Category,
  FareFamily,
  FinnairAmount,
  FinnairBoundFareFamily,
  FinnairBoundItem,
  FinnairCart,
  FinnairItineraryItemFlight,
  FinnairPassengerCode,
  FinnairPassengerItem,
  FinnairPassengerServiceSelectionItem,
  FinnairPricePerPassenger,
  FinnairServiceBoundItem,
  FinnairServiceItem,
  SubCategory,
} from '@fcom/dapi/api/models';
import { finShare } from '@fcom/rx/operators/finShare';
import {
  Amount,
  BoundType,
  CartState,
  FinnairServiceItemWithUpsell,
  LocationInfoMap,
  TravelerService,
} from '@fcom/dapi/interfaces';
import { getIconForServiceCategory, getIconForSubCategory } from '@fcom/dapi/utils';
import { ButtonMode, ButtonTheme, IconButtonSize } from '@fcom/ui-components/components/buttons';
import { ModalButtons } from '@fcom/ui-components/components/modal';
import { SentryLogger } from '@fcom/core/services';

import { BookingSummaryBound } from '../../interfaces';
import {
  filterServices,
  isBoundBasedCategory,
  sumOfPricesForTravelersServiceSelection,
} from '../../modules/ancillaries/utils';
import { getServicesForBoundWithUpsell, toAmount } from '../../utils/common-booking.utils';
import { getSummaryForCategory, ServiceType } from '../../utils/services-summary.utils';
import {
  CategoryStatusMap,
  categoryStatusMap,
  seatBundleCategoriesSelector,
  seatBundleSelectionsByFlightAndPassengerSelector,
  seatBundleSubCategoriesSelector,
  seatSelectionsForFlight,
  seatSelectionsForFlightAndTraveler,
  serviceCategories,
  serviceSelectionsForFragment,
  serviceSelectionsForFragmentAndTraveler,
} from '../../store';
import { ShoppingCartService } from '../../services/shopping-cart.service';
import { BENEFITS } from '../../utils/benefit.utils';
import {
  calculateDirtyTotal,
  calculatePriceForPassenger,
  calculateQuantityForPassenger,
  filterServiceItemByPassenger,
  getSelectedSeatNumbers,
  getServicesForBoundAndPassenger,
  hasMultipleVariants,
  multiplyLabelAmount,
} from '../../utils/shopping-cart.utils';

interface CartBound extends BookingSummaryBound {
  tailNames: string[];
}

interface ShoppingCartServiceItem {
  id: string;
  title?: string;
  titleKey?: string;
  titleParams?: ReplaceParams;
  count?: number;
  price?: FinnairAmount;
  icon?: SvgLibraryIcon;
  childProducts?: ShoppingCartServiceItem[];
}

interface FareFamilyModalData {
  isOpen: boolean;
  fareFamily?: FareFamily;
}

interface ItineraryModalData {
  isOpen: boolean;
  locations?: LocationInfoMap;
  bound?: FinnairBoundItem;
  boundType?: BoundType;
}

@Component({
  selector: 'fin-shopping-cart',
  standalone: false,
  templateUrl: './shopping-cart.component.html',
  styleUrl: './shopping-cart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShoppingCartComponent implements OnInit {
  protected readonly SvgLibraryIcon = SvgLibraryIcon;
  protected readonly IconButtonSize = IconButtonSize;
  protected readonly ModalButtons = ModalButtons;
  protected readonly ButtonTheme = ButtonTheme;
  protected readonly ButtonMode = ButtonMode;

  private store$ = inject<Store<BookingAndAppState>>(Store);
  private shoppingCartService = inject(ShoppingCartService);
  private destroyRef = inject(DestroyRef);
  sentryLogger = inject(SentryLogger);

  private readonly selectedSeatKey = 'shoppingCart.selectedSeat';

  @Input()
  selectedCategory: Category;

  isInsideModal = input(false);
  showModalCtaButton = input(false);
  modalCtaButtonText = input<string>();
  modalCtaButtonClick = output();

  currencyCode$: Observable<string>;
  ignoredBenefits = ['rebook', 'cancel', 'plusPoints'];
  offerBoundsPrices: WritableSignal<Amount[]> = signal([]);
  bounds$: Observable<CartBound[]>;
  cart$: Observable<FinnairCart>;
  cartTotal$: Observable<FinnairAmount>;
  selectedPassengerId$: BehaviorSubject<string | null> = new BehaviorSubject(null);
  selectedPassengerWithInfant$: Observable<boolean>;
  cartTotalPricePerPassenger$: Observable<Amount>;
  includedBenefits$: Observable<Record<string, ShoppingCartServiceItem[]>>;
  unpaidServices$: Observable<Record<string, ShoppingCartServiceItem[]>>;
  pendingServices$: Observable<Record<string, ShoppingCartServiceItem[]>>;
  serviceCategories = toSignal(this.store$.pipe(serviceCategories()));

  showPassengerTabs = signal(false);

  fareFamilyModalData: FareFamilyModalData = {
    isOpen: false,
  };

  itineraryModalData: ItineraryModalData = {
    isOpen: false,
  };

  priceForPassengerTypeCode(
    boundFareFamily: FinnairBoundFareFamily,
    cartPassengerTypeCode: FinnairPassengerCode
  ): FinnairPricePerPassenger | undefined {
    return boundFareFamily?.pricePerPassengers?.find((pricePerPax) =>
      pricePerPax.passengerTypeCodes.some((passengerTypeCode) => passengerTypeCode === cartPassengerTypeCode)
    );
  }

  ngOnInit(): void {
    this.currencyCode$ = this.store$.pipe(boundsCurrency());
    this.cart$ = this.store$.pipe(cartData(), finShare());

    const cartState$ = this.store$.pipe(cartState(), finShare());
    const seatBundleCategories$ = this.store$.select(seatBundleCategoriesSelector);
    const categoryStatus$ = this.store$.pipe(categoryStatusMap(), finShare());
    const pendingSelectionsByBound$ = this.createPendingSelectionsByBound$(cartState$);

    this.initOfferBoundsPrices();
    this.initCartTotal(cartState$, seatBundleCategories$, pendingSelectionsByBound$);
    this.initCartTotalPricePerPassenger(cartState$, seatBundleCategories$, pendingSelectionsByBound$);
    this.initSelectedPassengerWithInfant();
    this.initIncludedBenefits(categoryStatus$);
    this.initUnpaidServices(cartState$, categoryStatus$);
    this.initPendingServices(cartState$, pendingSelectionsByBound$);
    this.initBounds();
  }

  private initOfferBoundsPrices(): void {
    combineLatest([
      this.store$.pipe(selectedOutboundFareFamily()),
      this.store$.pipe(selectedInboundFareFamily()),
      this.selectedPassengerId$,
      this.currencyCode$,
      this.cart$,
    ])
      .pipe(
        filter(([outbound]) => Boolean(outbound)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([outbound, inbound, selectedPassengerId, currencyCode, cart]) => {
        if (!selectedPassengerId) {
          this.cartAndOfferAgreeOnTotal(cart, outbound, inbound);

          this.offerBoundsPrices.set(
            [
              toAmount({ amount: outbound?.price, currencyCode }),
              toAmount({ amount: inbound?.price, currencyCode }),
            ].filter((a) => a?.amount != null)
          );
        } else {
          const cartPassenger = cart.passengers.find(({ id }) => id === selectedPassengerId);
          const cartInfant = cart.passengers.find(
            ({ accompanyingTravelerId }) => accompanyingTravelerId === selectedPassengerId
          );

          const outboundPricePerPax = this.priceForPassengerTypeCode(outbound, cartPassenger.passengerTypeCode);
          const inboundPricePerPax = this.priceForPassengerTypeCode(inbound, cartPassenger.passengerTypeCode);

          const outboundInfantPricePerPax = cartInfant
            ? this.priceForPassengerTypeCode(outbound, FinnairPassengerCode.INF)
            : undefined;

          const inboundInfantPricePerPax = cartInfant
            ? this.priceForPassengerTypeCode(inbound, FinnairPassengerCode.INF)
            : undefined;

          this.cartAndOfferAgreeOnPerPaxTotal(
            cart,
            outbound,
            inbound,
            selectedPassengerId,
            cartPassenger.passengerTypeCode
          );

          this.offerBoundsPrices.set(
            [
              toAmount({ amount: outboundPricePerPax?.price + (outboundInfantPricePerPax?.price ?? 0), currencyCode }),
              inboundPricePerPax &&
                toAmount({ amount: inboundPricePerPax?.price + (inboundInfantPricePerPax?.price ?? 0), currencyCode }),
            ].filter((a) => a?.amount != null)
          );
        }
      });
  }

  private createPendingSelectionsByBound$(
    cartState$: Observable<CartState>
  ): Observable<TravelerService[][] | undefined> {
    return combineLatest([cartState$, this.selectedPassengerId$]).pipe(
      switchMap(([state, selectedPassengerId]) => {
        if (!state.dirty || !state?.cartData?.bounds || !this.selectedCategory) {
          return of(undefined);
        }
        return zip(state.cartData.bounds.map((bound) => this.getPendingSelectionsForBound(bound, selectedPassengerId)));
      }),
      finShare()
    );
  }

  private initCartTotal(
    cartState$: Observable<CartState>,
    seatBundleCategories$: Observable<Set<Category>>,
    pendingSelectionsByBound$: Observable<TravelerService[][] | undefined>
  ): void {
    this.cartTotal$ = combineLatest([this.cart$, cartState$, seatBundleCategories$, pendingSelectionsByBound$]).pipe(
      map(([cart, state, seatBundleCategories, pendingSelections]) => {
        if (!state.dirty || !this.selectedCategory || !pendingSelections) {
          return cart?.prices?.unpaid?.total?.total?.totalAmount;
        }
        return calculateDirtyTotal(cart, this.selectedCategory, seatBundleCategories, pendingSelections);
      })
    );
  }

  private initCartTotalPricePerPassenger(
    cartState$: Observable<CartState>,
    seatBundleCategories$: Observable<Set<Category>>,
    pendingSelectionsByBound$: Observable<TravelerService[][] | undefined>
  ): void {
    this.cartTotalPricePerPassenger$ = combineLatest([
      this.cart$,
      cartState$,
      this.selectedPassengerId$,
      seatBundleCategories$,
      pendingSelectionsByBound$,
    ]).pipe(
      map(([cart, state, passengerId, seatBundleCategories, pendingSelections]) => {
        if (state.dirty && this.selectedCategory && pendingSelections) {
          return calculateDirtyTotal(cart, this.selectedCategory, seatBundleCategories, pendingSelections, passengerId);
        }
        const unpaidTotal = cart?.prices?.unpaid?.total;
        if (passengerId && unpaidTotal?.totalPerPax?.[passengerId]) {
          const passengerTotal = unpaidTotal.totalPerPax[passengerId].totalAmount;
          const infant = cart.passengers.find(({ accompanyingTravelerId }) => accompanyingTravelerId === passengerId);
          const infantTotal = infant && unpaidTotal.totalPerPax[infant.id]?.totalAmount;
          if (infantTotal) {
            return {
              ...passengerTotal,
              amount: PreciseNumber.fromString(passengerTotal.amount)
                .add(PreciseNumber.fromString(infantTotal.amount))
                .toString(),
            };
          }
          return passengerTotal;
        }
        return unpaidTotal?.total?.totalAmount;
      }),
      distinctUntilChanged(),
      finShare()
    );
  }

  private initSelectedPassengerWithInfant(): void {
    this.selectedPassengerWithInfant$ = combineLatest([this.selectedPassengerId$, this.cart$]).pipe(
      map(([passengerId, cart]) => {
        if (!passengerId) {
          return false;
        }
        return Boolean(cart.passengers.find(({ accompanyingTravelerId }) => accompanyingTravelerId === passengerId));
      }),
      finShare()
    );
  }

  private initIncludedBenefits(categoryStatus$: Observable<CategoryStatusMap>): void {
    const includedServices$ = this.cart$.pipe(
      map((cart) => filterServices(cart?.services?.unpaid, (service) => service.includedInTicketType)),
      finShare()
    );
    this.includedBenefits$ = combineLatest([
      this.cart$,
      includedServices$,
      categoryStatus$,
      this.selectedPassengerId$,
    ]).pipe(
      filter(([cart]) => Boolean(cart?.bounds)),
      map(([cart, services, categoryStatus, passengerId]) =>
        cart.bounds.reduce((acc, bound) => {
          const benefits = this.mapBenefits(cart, services, categoryStatus, bound, passengerId, acc);
          const boundBenefits = benefits[bound.id] || [];
          return { ...benefits, [bound.id]: this.getRefundableAndChangeable(cart, bound).concat(boundBenefits) };
        }, {})
      )
    );
  }

  private initUnpaidServices(cartState$: Observable<CartState>, categoryStatus$: Observable<CategoryStatusMap>): void {
    this.unpaidServices$ = combineLatest([
      cartState$,
      categoryStatus$,
      this.selectedPassengerId$,
      this.store$.select(seatBundleCategoriesSelector),
      this.store$.select(seatBundleSubCategoriesSelector),
    ]).pipe(
      filter(([state]) => Boolean(state?.cartData?.bounds)),
      map(([state, categoryStatus, passengerId, seatBundleCategories, seatBundleSubCategories]) => {
        const cart = state.cartData;
        return cart.bounds.reduce(
          (acc, bound) => {
            const services = filterServices(
              cart.services?.unpaid,
              (service) => !service.includedInTicketType && !service.includedInTierBenefit
            );
            const serviceItems = getServicesForBoundWithUpsell(
              cart,
              false,
              services,
              cart.services.servicesOrder,
              bound,
              cart.serviceCatalog,
              categoryStatus
            );

            acc[bound.id] = serviceItems.reduce((items, categoryItem) => {
              const hiddenCategories =
                this.selectedCategory === Category.SEAT
                  ? [Category.SEAT, Category.BUNDLE, ...Array.from(seatBundleCategories)]
                  : [this.selectedCategory];
              if (!state.dirty || !hiddenCategories.includes(categoryItem.category)) {
                items.push(
                  this.buildServiceItem(
                    categoryItem,
                    bound,
                    passengerId,
                    cart.passengers,
                    seatBundleCategories,
                    seatBundleSubCategories
                  )
                );
              }
              return items.filter(Boolean);
            }, [] as ShoppingCartServiceItem[]);
            return acc;
          },
          {} as Record<string, ShoppingCartServiceItem[]>
        );
      })
    );
  }

  private initPendingServices(
    cartState$: Observable<CartState>,
    pendingSelectionsByBound$: Observable<TravelerService[][] | undefined>
  ): void {
    this.pendingServices$ = combineLatest([
      cartState$,
      pendingSelectionsByBound$,
      this.store$.select(seatBundleSubCategoriesSelector),
    ]).pipe(
      filter(([state, pendingSelections]) => state.dirty && !!pendingSelections && Boolean(state?.cartData?.bounds)),
      map(([state, pendingSelections, seatBundleSubCategories]) =>
        this.mapSelectionsToBounds(state.cartData.bounds, pendingSelections, seatBundleSubCategories)
      )
    );
  }

  private initBounds(): void {
    this.bounds$ = combineLatest([
      this.store$.pipe(currentOfferSelection()),
      this.store$.pipe(globalBookingFlights()),
    ]).pipe(
      filter(([offers]) => offers && Boolean(offers.length)),
      map(([offers, flights]): CartBound[] =>
        offers.map((offer, index) => {
          return {
            ...offer,
            arrival: {
              ...offer.arrival,
              cityName: flights[index]?.destination.cityName,
            },
            departure: {
              ...offer.departure,
              cityName: flights[index]?.origin.cityName,
            },
            tailNames: resolveTailNames(offer.operatingAirlineCodes),
          };
        })
      )
    );
  }

  closeShoppingCart(): void {
    this.selectedPassengerId$.next(null);
    this.shoppingCartService.setVisible(false);
  }

  openFareFamilyDetails(fareFamily: FareFamily): void {
    this.fareFamilyModalData.isOpen = true;
    this.fareFamilyModalData.fareFamily = fareFamily;
  }

  openItineraryDetails(
    bound: FinnairBoundItem,
    bounds: FinnairBoundItem[],
    index: number,
    locations: LocationInfoMap
  ): void {
    this.itineraryModalData.isOpen = true;
    this.itineraryModalData.bound = bound;
    this.itineraryModalData.boundType =
      bounds.length > 2 ? BoundType.multi : index === 0 ? BoundType.outbound : BoundType.inbound;
    this.itineraryModalData.locations = locations;
  }

  selectPassenger(passengerId: string | null): void {
    this.selectedPassengerId$.next(passengerId);
  }

  handleModalCtaButtonClick(): void {
    this.modalCtaButtonClick.emit();
  }

  private buildServiceItem(
    categoryItem: FinnairServiceItemWithUpsell,
    bound: FinnairBoundItem,
    passengerId: string | null,
    cartPassengers: FinnairPassengerItem[],
    seatBundleCategories: Set<Category>,
    seatBundleSubCategories: Set<SubCategory>
  ): ShoppingCartServiceItem | null {
    const filteredItem = filterServiceItemByPassenger(categoryItem, passengerId, cartPassengers);
    const filteredCategoryItem: FinnairServiceItemWithUpsell = { ...categoryItem, bounds: filteredItem.bounds };
    const boundData = filteredItem.bounds.find((itemBound) => itemBound.id === bound.id);
    const services = getServicesForBoundAndPassenger(boundData, passengerId);
    const multipleVariants = hasMultipleVariants(services);

    if (seatBundleCategories.has(categoryItem.category) || categoryItem.category === Category.BUNDLE) {
      return this.buildComboItem(filteredCategoryItem, services, boundData, seatBundleSubCategories);
    } else if (multipleVariants) {
      return this.buildCategoryItem(filteredCategoryItem, boundData, passengerId);
    }
    return this.buildSingleServiceItem(filteredCategoryItem, boundData, services);
  }

  private buildComboItem(
    categoryItem: FinnairServiceItemWithUpsell,
    services: FinnairPassengerServiceSelectionItem[],
    boundData: FinnairServiceBoundItem,
    bundleSubCategories: Set<SubCategory>
  ): ShoppingCartServiceItem {
    if (services.length === 0) {
      return null;
    }

    const count = services.reduce((totalQuantity, { quantity }) => totalQuantity + (quantity ?? 1), 0);
    const price: FinnairAmount = {
      ...boundData.totalPrice,
      amount: String(
        services.reduce(
          (sum, service) => sum.add(PreciseNumber.fromString(service.totalPrice?.amount ?? '0')),
          PreciseNumber.ZERO
        )
      ),
    };
    return this.buildShoppingCartItem({
      title: categoryItem.title,
      count,
      price,
      icon: SvgLibraryIcon.DISCOUNTED_PRICE,
      childProducts: this.buildComboServiceItems(categoryItem, services, bundleSubCategories),
    });
  }

  private buildComboServiceItems(
    categoryItem: FinnairServiceItemWithUpsell,
    services: FinnairPassengerServiceSelectionItem[],
    bundleSubCategories: Set<SubCategory>
  ): ShoppingCartServiceItem[] {
    if (bundleSubCategories.has(services[0].subCategory)) {
      const count = services.reduce((totalQuantity, { quantity }) => totalQuantity + (quantity ?? 1), 0);
      const seatNumbers = getSelectedSeatNumbers(categoryItem);
      return [
        this.buildShoppingCartItem({
          titleKey: this.selectedSeatKey,
          count,
          titleParams: seatNumbers.length > 0 ? [seatNumbers.join(', ')] : undefined,
        }),
        this.buildShoppingCartItem({
          titleKey: 'shoppingCart.comboProducts.' + services[0].subCategory,
          count,
        }),
      ];
    }
    return services.map(({ displayName, quantity }) =>
      this.buildShoppingCartItem({
        title: displayName,
        count: quantity,
      })
    );
  }

  private buildCategoryItem(
    categoryItem: FinnairServiceItemWithUpsell,
    boundData: FinnairServiceBoundItem,
    passengerId: string | null
  ): ShoppingCartServiceItem | null {
    const quantity = passengerId ? calculateQuantityForPassenger(boundData) : boundData.quantity;
    const price = passengerId ? calculatePriceForPassenger(boundData) : boundData.totalPrice;

    if (quantity <= 0) {
      return null;
    }

    const seatNumbers = getSelectedSeatNumbers(categoryItem);

    return this.buildShoppingCartItem({
      title: categoryItem.category !== Category.SEAT ? categoryItem.title : undefined,
      titleKey: categoryItem.category === Category.SEAT ? this.selectedSeatKey : undefined,
      titleParams: categoryItem.category === Category.SEAT ? [seatNumbers.join(', ')] : undefined,
      count: quantity,
      price: price,
      icon: getIconForServiceCategory(categoryItem.category),
    });
  }

  private buildSingleServiceItem(
    categoryItem: FinnairServiceItemWithUpsell,
    boundData: FinnairServiceBoundItem,
    services: FinnairPassengerServiceSelectionItem[]
  ): ShoppingCartServiceItem | null {
    const totalCount = services.reduce((totalQuantity, { quantity }) => totalQuantity + (quantity ?? 0), 0);

    if (totalCount <= 0) {
      return null;
    }

    const totalPrice: FinnairAmount = {
      ...boundData.totalPrice,
      amount: String(
        services.reduce(
          (sum, service) => sum.add(PreciseNumber.fromString(service.totalPrice?.amount ?? '0')),
          PreciseNumber.ZERO
        )
      ),
    };

    const title = services[0]?.displayName ?? categoryItem.title;
    const seatNumbers = getSelectedSeatNumbers(categoryItem);
    const displayTitle = seatNumbers.length > 0 ? `${title} (${seatNumbers.join(', ')})` : title;

    return this.buildShoppingCartItem({
      title: displayTitle,
      count: totalCount,
      price: totalPrice,
      icon: getIconForSubCategory(services[0].subCategory),
    });
  }

  private mapBenefits(
    cart: FinnairCart,
    services: FinnairServiceItem[],
    categoryStatus: CategoryStatusMap,
    bound: FinnairBoundItem,
    passengerId: string | null,
    acc: Record<string, ShoppingCartServiceItem[]>
  ): Record<string, ShoppingCartServiceItem[]> {
    const flight = bound.itinerary[0] as FinnairItineraryItemFlight;
    if (!isFinnairNorraOrWetLease(flight.operatingAirline.name, flight.operatingAirline.code)) {
      return this.mapPartnerBenefits(cart, bound, acc);
    }
    const serviceItems = getServicesForBoundWithUpsell(
      cart,
      false,
      services,
      cart.services.servicesOrder,
      bound,
      cart.serviceCatalog,
      categoryStatus
    );
    const isShortHaul = bound?.itinerary.every((item: FinnairItineraryItemFlight) => item.isShortHaul);
    acc[bound.id] = serviceItems.reduce((boundAcc, item) => {
      const filteredItem = filterServiceItemByPassenger(item, passengerId, cart.passengers);
      const serviceSummary = getSummaryForCategory(item.category, bound, filteredItem, isShortHaul);
      return serviceSummary
        .filter((summaryItem) => summaryItem.type !== ServiceType.NON_INCLUDED)
        .reduce((summaryAcc, { quantity, text, description }) => {
          summaryAcc.push(
            this.buildShoppingCartItem({
              title: text,
              titleKey: description,
              count: quantity,
            })
          );
          return summaryAcc;
        }, boundAcc);
    }, [] as ShoppingCartServiceItem[]);
    return acc;
  }

  private mapPartnerBenefits(
    cart: FinnairCart,
    bound: FinnairBoundItem,
    acc: Record<string, ShoppingCartServiceItem[]>
  ): Record<string, ShoppingCartServiceItem[]> {
    const hasOnlyAdults = cart.passengers.every((pax) => pax.passengerTypeCode === FinnairPassengerCode.ADT);
    if (hasOnlyAdults) {
      const fareFamily = cart.benefits[bound.fareFamily.code];
      const benefits = fareFamily.translatedBenefitRows
        .filter((benefit) => benefit.isPositive && !this.ignoredBenefits.includes(benefit.key))
        .flatMap((benefit) => (benefit.children ? benefit.children : benefit));
      const passengerCount = cart.passengers.length;
      acc[bound.id] = benefits.map((benefit) =>
        this.buildShoppingCartItem({
          title: multiplyLabelAmount(benefit.label, passengerCount),
        })
      );
    } else {
      acc[bound.id] = [
        this.buildShoppingCartItem({
          titleKey: 'shoppingCart.partnerAirlineAllowances',
        }),
      ];
    }
    return acc;
  }

  private getRefundableAndChangeable(cart: FinnairCart, bound: FinnairBoundItem): ShoppingCartServiceItem[] {
    const fareFamily = cart.benefits[bound.fareFamily?.code];
    const refundable = fareFamily?.translatedBenefitRows?.find((row) => row.key === BENEFITS.cancel);
    const changeable = fareFamily?.translatedBenefitRows?.find((row) => row.key === BENEFITS.rebook);
    const result: ShoppingCartServiceItem[] = [];

    if (refundable) {
      result.push(this.buildShoppingCartItem({ title: refundable.label }));
    }

    if (changeable) {
      result.push(this.buildShoppingCartItem({ title: changeable.label }));
    }

    return result;
  }

  private buildShoppingCartItem(itemData: Partial<ShoppingCartServiceItem>): ShoppingCartServiceItem {
    return {
      id: uuid(),
      ...itemData,
    };
  }

  private getPendingSelectionsForBound(
    bound: FinnairBoundItem,
    selectedPassengerId: string | null
  ): Observable<TravelerService[]> {
    const flightId = (bound.itinerary[0] as FinnairItineraryItemFlight).id;

    if (this.selectedCategory === Category.SEAT) {
      return this.getSeatSelectionsForBound(flightId, selectedPassengerId);
    }

    const id = isBoundBasedCategory(this.selectedCategory) ? bound.id : flightId;
    if (selectedPassengerId) {
      return this.store$.select(
        serviceSelectionsForFragmentAndTraveler(id, selectedPassengerId, this.selectedCategory)
      );
    }
    return this.store$.pipe(
      serviceSelectionsForFragment<TravelerService[]>(id, this.selectedCategory),
      map((selections) => Object.values(selections).flat())
    );
  }

  private getSeatSelectionsForBound(
    flightId: string,
    selectedPassengerId: string | null
  ): Observable<TravelerService[]> {
    if (selectedPassengerId) {
      return combineLatest([
        this.store$.pipe(seatSelectionsForFlightAndTraveler(flightId, selectedPassengerId)),
        this.store$.select(seatBundleSelectionsByFlightAndPassengerSelector),
      ]).pipe(
        map(([seatSelection, bundleSelections]) => {
          if (bundleSelections?.[flightId]?.[selectedPassengerId]?.length > 0) {
            return bundleSelections[flightId][selectedPassengerId];
          }
          return seatSelection?.seatNumber ? [this.buildSeatTravelerService(seatSelection)] : [];
        })
      );
    }

    return combineLatest([
      this.store$.select(seatSelectionsForFlight(flightId)),
      this.store$.select(seatBundleSelectionsByFlightAndPassengerSelector),
    ]).pipe(
      map(([seatSelections, bundleSelections]) => {
        const allBundleSelections = bundleSelections
          ? Object.values(bundleSelections[flightId] || {})
              .flat()
              .filter(Boolean)
          : [];
        if (!seatSelections && allBundleSelections.length === 0) {
          return [];
        }
        if (!seatSelections) {
          return allBundleSelections;
        }
        const validSeatSelections = Object.values(seatSelections).filter((selection) => selection?.seatNumber);
        return validSeatSelections.length > 0
          ? [
              ...validSeatSelections.map((selection) => this.buildSeatTravelerService(selection)),
              ...allBundleSelections,
            ]
          : [...allBundleSelections];
      })
    );
  }

  private buildSeatTravelerService(seatSelection: SeatSelectionForFlight): TravelerService {
    return {
      ...seatSelection,
      category: SubCategory.SEAT,
      quantity: 1,
      variant: seatSelection.seatNumber,
      displayName: null,
      titleKey: this.selectedSeatKey,
      titleParams: [seatSelection.seatNumber],
      includedInTicketType: false,
      includedInTierBenefit: false,
    } as unknown as TravelerService;
  }

  private mapSelectionsToBounds(
    bounds: FinnairBoundItem[],
    selectionsByBound: TravelerService[][],
    seatBundleSubCategories: Set<SubCategory>
  ): Record<string, ShoppingCartServiceItem[]> {
    return bounds.reduce(
      (acc, bound, index) => {
        const boundSelections = selectionsByBound[index];
        const bundleSelections =
          boundSelections?.filter((selection) => seatBundleSubCategories.has(selection.category)) ?? [];
        const otherSelections =
          boundSelections?.filter((selection) => !seatBundleSubCategories.has(selection.category)) ?? [];

        const boundItems = [];

        if (otherSelections.length === 1) {
          boundItems.push(
            this.buildShoppingCartItem({
              ...otherSelections[0],
              title: otherSelections[0].displayName,
              count: otherSelections[0].quantity,
              price: sumOfPricesForTravelersServiceSelection({
                [otherSelections[0].travelerId]: otherSelections,
              }),
              icon: getIconForSubCategory(otherSelections[0].category),
            })
          );
        } else if (otherSelections.length > 1) {
          const areSeats = otherSelections[0].category === SubCategory.SEAT;
          const title = areSeats
            ? undefined
            : this.serviceCategories().find((serviceCategory) => serviceCategory.category === this.selectedCategory)
                ?.translations?.title;
          boundItems.push(
            this.buildShoppingCartItem({
              title,
              titleKey: areSeats ? this.selectedSeatKey : undefined,
              titleParams: areSeats ? [otherSelections.map((s) => s.seatNumber).join(', ')] : undefined,
              count: otherSelections.reduce((total, selection) => total + (selection.quantity ?? 0), 0),
              price: sumOfPricesForTravelersServiceSelection({
                [otherSelections[0].travelerId]: otherSelections,
              }),
              icon: getIconForServiceCategory(this.selectedCategory),
            })
          );
        }
        if (bundleSelections.length) {
          boundItems.push(...this.buildBundleShoppingCartItems(bundleSelections));
        }
        acc[bound.id] = boundItems;
        return acc;
      },
      {} as Record<string, ShoppingCartServiceItem[]>
    );
  }

  private buildBundleShoppingCartItems(boundSelections: TravelerService[]): ShoppingCartServiceItem[] {
    const category = this.serviceCategories().find(
      (serviceCategory) => serviceCategory.category === Category.SEAT_WIFI
    );
    const count = boundSelections?.length ?? 0;
    if (count === 0) {
      return [];
    }
    const totalAmount = boundSelections.reduce(
      (sum, selection) => sum.add(PreciseNumber.fromString(selection.pricePerItem.amount)),
      PreciseNumber.ZERO
    );
    const seatNumbers = boundSelections.map((selection) => selection.seatNumber);
    return [
      this.buildShoppingCartItem({
        title: category?.translations.title,
        count,
        price: {
          amount: totalAmount.toString(),
          currencyCode: boundSelections[0].pricePerItem.currencyCode,
        },
        icon: SvgLibraryIcon.DISCOUNTED_PRICE,
        childProducts: [
          this.buildShoppingCartItem({
            titleKey: this.selectedSeatKey,
            count,
            titleParams: [seatNumbers.join(', ')],
          }),
          this.buildShoppingCartItem({
            titleKey: 'shoppingCart.comboProducts.wifi',
            count,
          }),
        ],
      }),
    ];
  }

  private cartAndOfferAgreeOnTotal(
    cart: FinnairCart,
    outbound: FinnairBoundFareFamily,
    inbound: FinnairBoundFareFamily | undefined
  ) {
    const cartFlightTotalAmount = PreciseNumber.fromString(
      cart.prices?.unpaid?.flight?.total?.totalAmount?.amount ?? '0'
    );
    if (cartFlightTotalAmount.equals(PreciseNumber.ZERO)) {
      return;
    }
    const outboundPrice = PreciseNumber.fromString(outbound.price);
    const inboundPrice = PreciseNumber.fromString(inbound?.price ?? '0');
    const offerFlightTotal = outboundPrice.add(inboundPrice);

    if (cartFlightTotalAmount.inequal(offerFlightTotal)) {
      this.sentryLogger.error(
        `Cart ${cart.id} flight total ${cartFlightTotalAmount.toString()} does not match offer total ${offerFlightTotal.toString()} with outbound ${outboundPrice.toString()} for ${outbound.id} and inbound ${inboundPrice.toString()} for ${inbound?.id}`
      );
    }
  }

  private cartAndOfferAgreeOnPerPaxTotal(
    cart: FinnairCart,
    outbound: FinnairBoundFareFamily,
    inbound: FinnairBoundFareFamily | undefined,
    selectedPassengerId: string,
    passengerTypeCode: FinnairPassengerCode
  ) {
    const cartFlightTotalAmountForPax = PreciseNumber.fromString(
      cart.prices?.unpaid?.flight.totalPerPax?.[selectedPassengerId]?.totalAmount?.amount ?? '0'
    );
    if (cartFlightTotalAmountForPax.equals(PreciseNumber.ZERO)) {
      return;
    }
    const outboundPricePerPax = this.priceForPassengerTypeCode(outbound, passengerTypeCode);
    const inboundPricePerPax = this.priceForPassengerTypeCode(inbound, passengerTypeCode);

    const offerPerPaxTotal = PreciseNumber.fromString(
      (outboundPricePerPax.price + (inboundPricePerPax?.price ?? 0)).toFixed(2)
    );

    if (cartFlightTotalAmountForPax.inequal(offerPerPaxTotal)) {
      this.sentryLogger.error(
        `Cart ${cart.id} flight total ${cartFlightTotalAmountForPax.toString()} for passenger ${selectedPassengerId} does not match offer pax total ${offerPerPaxTotal.toString()} with outbound ${outboundPricePerPax.price} for ${outbound.id} and inbound ${inboundPricePerPax.price} for ${inbound?.id}`
      );
    }
  }
}
